/**
 * API Configuration Utility
 * Handles flexible port detection and API endpoint management
 */

// Environment-based configuration
const isProduction = process.env.NODE_ENV === 'production';

// Default ports to try - prioritize backend port 3001 (for development)
const DEFAULT_PORTS = [3001, 3000];

// Cache for the working API base URL
let cachedApiBaseUrl = null;

// Production API base URL (will be set during build)
const PRODUCTION_API_URL = process.env.REACT_APP_API_URL || window.location.origin;

/**
 * Test if a port is available by making a health check request
 * @param {number} port - Port number to test
 * @returns {Promise<boolean>} - True if port is available
 */
const testPort = async (port) => {
  try {
    const response = await fetch(`http://localhost:${port}/api/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // Short timeout to avoid long waits
      signal: AbortSignal.timeout(2000)
    });
    
    // Check if we get a successful response from the health endpoint
    if (response.status === 200) {
      const data = await response.json();
      // Verify this is actually our backend API server
      return data.status === 'healthy' && data.message === 'API server is running';
    }
    return false;
  } catch (error) {
    // Network error, timeout, or other issues mean port is not available
    return false;
  }
};

/**
 * Find the first available API port
 * @returns {Promise<string>} - Base URL of the working API server
 */
const findWorkingPort = async () => {
  // If we already have a cached working URL, test it first
  if (cachedApiBaseUrl) {
    const port = parseInt(cachedApiBaseUrl.split(':').pop());
    if (await testPort(port)) {
      return cachedApiBaseUrl;
    }
    // Cached URL is no longer working, clear it
    cachedApiBaseUrl = null;
  }

  // Try each port in order
  for (const port of DEFAULT_PORTS) {
    if (await testPort(port)) {
      const baseUrl = `http://localhost:${port}`;
      cachedApiBaseUrl = baseUrl;
      console.log(`✅ API server found on port ${port}`);
      return baseUrl;
    }
  }

  // No working port found
  throw new Error('No API server found on any of the configured ports');
};

/**
 * Get the current API base URL
 * @returns {Promise<string>} - Base URL of the working API server
 */
export const getApiBaseUrl = async () => {
  // In production, use the production API URL
  if (isProduction) {
    return PRODUCTION_API_URL;
  }
  
  // In development, try to find working port
  try {
    return await findWorkingPort();
  } catch (error) {
    console.error('Failed to find working API port:', error);
    // Fallback to default port
    return `http://localhost:${DEFAULT_PORTS[0]}`;
  }
};

/**
 * Clear the cached API base URL (useful for testing or when servers restart)
 */
export const clearApiCache = () => {
  cachedApiBaseUrl = null;
};

/**
 * Get API endpoint URL
 * @param {string} endpoint - API endpoint path (e.g., '/api/projects')
 * @returns {Promise<string>} - Full URL for the endpoint
 */
export const getApiUrl = async (endpoint) => {
  const baseUrl = await getApiBaseUrl();
  return `${baseUrl}${endpoint}`;
};

/**
 * Create an axios instance with automatic port detection
 * @returns {Promise<import('axios').AxiosInstance>} - Configured axios instance
 */
export const createApiClient = async () => {
  const axios = (await import('axios')).default;
  const baseURL = await getApiBaseUrl();
  
  return axios.create({
    baseURL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

/**
 * Health check function to verify API connectivity
 * @returns {Promise<{status: string, port: number, url: string}>} - Health check result
 */
export const healthCheck = async () => {
  try {
    const baseUrl = await getApiBaseUrl();
    const port = parseInt(baseUrl.split(':').pop());
    
    const response = await fetch(`${baseUrl}/api/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: AbortSignal.timeout(5000)
    });

    const data = await response.json();

    return {
      status: 'healthy',
      port,
      url: baseUrl,
      responseStatus: response.status,
      serverInfo: data
    };
  } catch (error) {
    return {
      status: 'unhealthy',
      port: null,
      url: null,
      error: error.message
    };
  }
};

// Export default configuration
const apiConfig = {
  getApiBaseUrl,
  getApiUrl,
  createApiClient,
  healthCheck,
  clearApiCache,
  DEFAULT_PORTS
};

export default apiConfig;
